import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle2, Briefcase, Brain, Wrench, AlertTriangle, Heart, HeartOff, Sparkles, BookOpen, Code, Target, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const sectionMeta = {
  'what you will try': { icon: BookOpen, color: 'teal', bg: 'from-teal-50 to-emerald-50', border: 'border-teal-200', iconBg: 'bg-teal-100', iconColor: 'text-teal-600', bullet: CheckCircle2 },
  'commonly used for': { icon: Briefcase, color: 'blue', bg: 'from-blue-50 to-indigo-50', border: 'border-blue-200', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', bullet: Zap },
  'feels to learn': { icon: Sparkles, color: 'purple', bg: 'from-purple-50 to-fuchsia-50', border: 'border-purple-200', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', bullet: Sparkles },
  'skills this sampler': { icon: Target, color: 'orange', bg: 'from-orange-50 to-amber-50', border: 'border-orange-200', iconBg: 'bg-orange-100', iconColor: 'text-orange-600', bullet: Target },
  'tools you would': { icon: Wrench, color: 'slate', bg: 'from-slate-50 to-gray-50', border: 'border-slate-200', iconBg: 'bg-slate-100', iconColor: 'text-slate-600', bullet: Code },
  'common challenges': { icon: AlertTriangle, color: 'red', bg: 'from-red-50 to-rose-50', border: 'border-red-200', iconBg: 'bg-red-100', iconColor: 'text-red-500', bullet: AlertTriangle },
  'usually enjoys': { icon: Heart, color: 'green', bg: 'from-green-50 to-emerald-50', border: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-600', bullet: Heart },
  'may not enjoy': { icon: HeartOff, color: 'gray', bg: 'from-gray-50 to-stone-50', border: 'border-gray-300', iconBg: 'bg-gray-100', iconColor: 'text-gray-500', bullet: HeartOff },
  'after this sampler': { icon: Brain, color: 'teal', bg: 'from-teal-50 to-cyan-50', border: 'border-teal-200', iconBg: 'bg-teal-100', iconColor: 'text-teal-600', bullet: Brain },
};

function getSectionMeta(title) {
  const lower = title.toLowerCase();
  for (const [key, meta] of Object.entries(sectionMeta)) {
    if (lower.includes(key)) return meta;
  }
  return { icon: BookOpen, color: 'teal', bg: 'from-gray-50 to-white', border: 'border-gray-200', iconBg: 'bg-gray-100', iconColor: 'text-gray-600', bullet: CheckCircle2 };
}

const HEADER_PATTERNS = [
  'what you will try',
  'commonly used for',
  'feels to learn',
  'skills this sampler',
  'tools you would',
  'common challenges',
  'who usually enjoys',
  'who may not enjoy',
  'after this sampler',
];

function isKnownHeader(text) {
  const lower = text.toLowerCase();
  return HEADER_PATTERNS.some(p => lower.includes(p));
}

function parseContent(bodyMd) {
  if (!bodyMd) return { intro: [], sections: [] };

  const blocks = bodyMd.split('\n\n').map(b => b.trim()).filter(Boolean);
  const intro = [];
  const sections = [];
  let currentSection = null;

  for (const block of blocks) {
    const line = block.replace(/\n/g, ' ').trim();
    if (!line) continue;

    const lower = line.toLowerCase();
    const isIntro = (lower.startsWith('what this sampler') || lower.startsWith('this sampler')) && sections.length === 0;

    if (isIntro) {
      intro.push(line);
    } else if (isKnownHeader(line)) {
      if (currentSection) sections.push(currentSection);
      currentSection = { title: line, items: [] };
    } else if (currentSection) {
      currentSection.items.push(line);
    } else {
      intro.push(line);
    }
  }
  if (currentSection) sections.push(currentSection);

  return { intro, sections };
}

const SectionCard = ({ section, index }) => {
  const meta = getSectionMeta(section.title);
  const Icon = meta.icon;
  const BulletIcon = meta.bullet;
  const isReflection = section.title.toLowerCase().includes('after this sampler');

  if (isReflection) {
    return (
      <div data-testid={`section-reflection`} className="mt-12 p-8 md:p-10 bg-gradient-to-br from-teal-600 to-blue-600 rounded-2xl text-white text-center">
        <Brain className="mx-auto mb-4 opacity-80" size={36} />
        <h3 className="text-xl md:text-2xl font-bold mb-4">{section.title}</h3>
        {section.items.map((item, i) => (
          <p key={i} className={`text-white/90 ${item.startsWith('"') ? 'text-lg md:text-xl font-semibold mt-3 italic' : 'text-base'}`}>
            {item.replace(/^""+|""+$/g, '"')}
          </p>
        ))}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Button asChild size="lg" variant="secondary" className="text-base font-semibold">
            <Link to="/tests">Take the Full Test</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base bg-white/10 border-white/30 text-white hover:bg-white/20">
            <Link to="/pathways/after-10th">Explore Other Samplers</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isCompare = section.title.toLowerCase().includes('usually enjoys') || section.title.toLowerCase().includes('may not enjoy');
  
  return (
    <div
      data-testid={`section-${index}`}
      className={`rounded-2xl border ${meta.border} bg-gradient-to-br ${meta.bg} p-6 md:p-8 transition-all hover:shadow-md`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-xl ${meta.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={meta.iconColor} size={20} />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-gray-900">{section.title}</h3>
      </div>

      {isCompare ? (
        <div className="flex flex-wrap gap-3">
          {section.items.map((item, i) => (
            <Badge key={i} variant="secondary" className={`px-3 py-1.5 text-sm font-medium ${meta.iconBg} ${meta.iconColor} border ${meta.border}`}>
              <BulletIcon size={14} className="mr-1.5 flex-shrink-0" />
              {item}
            </Badge>
          ))}
        </div>
      ) : (
        <ul className="space-y-3">
          {section.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <BulletIcon size={16} className={`${meta.iconColor} mt-0.5 flex-shrink-0`} />
              <span className="text-gray-700 text-sm md:text-base">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('pages')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') setError('Page not found');
          else setError(fetchError.message || 'Failed to load page');
          setPage(null);
        } else {
          setPage(data);
        }
      } catch (err) {
        setError('Failed to load page');
        setPage(null);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPage();
  }, [slug]);

  const parsed = useMemo(() => {
    if (!page?.body_md) return { intro: [], sections: [] };
    return parseContent(page.body_md);
  }, [page]);

  const langHint = slug?.includes('python') ? 'python' : slug?.includes('java') ? 'java' : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading page...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
            <p className="text-gray-600 mb-6">The page you're looking for doesn't exist or has been unpublished.</p>
            <Button asChild className="btn-primary">
              <Link to="/"><ArrowLeft size={16} className="mr-2" />Back to Home</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="dynamic-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-16 md:pt-20">
        <div className={`relative w-full py-16 md:py-24 ${langHint === 'python' ? 'bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700' : langHint === 'java' ? 'bg-gradient-to-br from-orange-500 via-red-500 to-rose-600' : 'bg-gradient-to-br from-teal-600 to-blue-600'}`}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center text-white">
            <Badge className="mb-4 bg-white/20 text-white border-white/30 text-sm px-4 py-1">
              Free Sampler
            </Badge>
            <h1 data-testid="page-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              {page.title}
            </h1>
            {page.meta_description && (
              <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto">
                {page.meta_description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-4 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/pathways/after-10th"
            className="inline-flex items-center text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to After 10th / Inter
          </Link>
        </div>
      </div>

      {/* Intro */}
      {parsed.intro.length > 0 && (
        <section className="py-10 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 md:p-8">
              {parsed.intro.map((text, i) => (
                <p key={i} className="text-gray-700 text-base md:text-lg leading-relaxed mb-3 last:mb-0">
                  {text.replace(/^what this sampler is:\s*/i, '').replace(/^what this sampler:\s*/i, '')}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content Sections */}
      <section className="pb-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          {(() => {
            const regular = parsed.sections.filter(s => {
              const l = s.title.toLowerCase();
              return !l.includes('usually enjoys') && !l.includes('may not enjoy') && !l.includes('after this sampler');
            });
            const enjoys = parsed.sections.find(s => s.title.toLowerCase().includes('usually enjoys'));
            const notEnjoys = parsed.sections.find(s => s.title.toLowerCase().includes('may not enjoy'));
            const reflection = parsed.sections.find(s => s.title.toLowerCase().includes('after this sampler'));

            return (
              <>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {regular.map((section, i) => (
                    <SectionCard key={i} section={section} index={i} />
                  ))}
                </div>

                {/* Who Enjoys / Who Doesn't comparison */}
                {(enjoys || notEnjoys) && (
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {enjoys && <SectionCard section={enjoys} index={regular.length} />}
                    {notEnjoys && <SectionCard section={notEnjoys} index={regular.length + 1} />}
                  </div>
                )}

                {/* Reflection CTA */}
                {reflection && <SectionCard section={reflection} index={regular.length + 2} />}
              </>
            );
          })()}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 px-4 md:px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Ready to explore more?</h3>
          <p className="text-gray-600 mb-6">Take our 4 tests to find your perfect career path, or try another sampler.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="btn-primary">
              <Link to="/tests">Start 4 Tests</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pathways/after-10th">Browse All Samplers</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DynamicPage;
